import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/api';

const ClientEnrollmentAcknowledgement = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  useEffect(() => {
    // Redirect if not a client or not enrolled by a clinician
    if (user?.role !== 'client' || !user?.invitationSentBy) {
      navigate('/dashboard');
      return;
    }

    // Redirect if already acknowledged
    if (user?.clientEnrollmentAcknowledgement?.acknowledged) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50;
    if (bottom && !hasScrolled) {
      setHasScrolled(true);
    }
  };

  const handleAcknowledge = async () => {
    if (!hasScrolled) {
      setError('Please scroll to the bottom of the document before acknowledging.');
      return;
    }

    if (!acknowledged) {
      setError('Please check the acknowledgement box to continue.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await userService.acknowledgeEnrollment(user._id);

      // Refresh user data
      await refreshUser();

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.message || '';

      // Backward-compatible fallback for older backend behavior
      if (err.response?.status === 400 && /already acknowledged/i.test(message)) {
        await refreshUser();
        navigate('/dashboard');
        return;
      }

      console.error('Error acknowledging enrollment:', err);
      setError(message || 'Failed to acknowledge enrollment. Please try again.');
      setLoading(false);
    }
  };

  const handleDecline = () => {
    setShowDeclineModal(true);
  };

  const confirmDecline = () => {
    // Logout the user and redirect to login page
    logout();
    navigate('/login');
  };

  const cancelDecline = () => {
    setShowDeclineModal(false);
  };

  const enrolledBy = user?.clientEnrollmentAcknowledgement?.enrolledBy || 'your healthcare provider';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-t-lg">
          <h1 className="text-3xl font-bold mb-2">Client Enrollment Acknowledgment</h1>
          <p className="text-blue-100">MHDF Momentum Mental Wellness Companion</p>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <span className="bg-white/20 px-3 py-1 rounded">Enrolled by: {enrolledBy}</span>
            <span className="bg-red-500 px-3 py-1 rounded font-semibold">Required Acknowledgment</span>
          </div>
        </div>

        {/* Content */}
        <div 
          className="p-8 max-h-[60vh] overflow-y-auto prose prose-sm max-w-none"
          onScroll={handleScroll}
        >
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <p className="font-semibold text-yellow-800">
              ⚠️ Important: Please read this document carefully before beginning your mental wellness companion program.
            </p>
            <p className="text-yellow-700 mt-2">
              You have been enrolled in the MHDF Momentum program by {enrolledBy}. Before you can access your program content, you must review and acknowledge these terms.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to MHDF Momentum</h2>
          
          <p className="mb-4">
            You have been enrolled in the MHDF Momentum mental wellness companion program by {enrolledBy}. This 365-day structured program is designed to support your journey toward mental wellness and spiritual growth.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Program Overview</h3>
          <p className="mb-4">
            The MHDF Momentum program provides:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Daily evidence-based mental wellness content</li>
            <li>Progress tracking and mood journaling</li>
            <li>Optional faith-integrated content</li>
            <li>Safety planning and crisis resources</li>
            <li>Regular check-ins with your assigned provider</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Your Care Relationship</h3>
          <p className="mb-4">
            You are enrolled as an active client of {enrolledBy}. This means:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Your assigned provider can monitor your progress and engagement</li>
            <li>You can share journal entries and progress data with your provider (with your consent)</li>
            <li>Your provider may send you messages and check-ins through the platform</li>
            <li>All interactions are HIPAA-compliant and confidential</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Privacy & Consent</h3>
          <p className="mb-4">
            Your privacy is protected:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>You control what information you share with your provider</li>
            <li>You can adjust your consent preferences at any time in your profile settings</li>
            <li>Journal entries are private unless you explicitly share them</li>
            <li>Progress data sharing requires your active consent</li>
            <li>You will be prompted before any data is shared with your provider</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Program Expectations</h3>
          <p className="mb-4">
            To get the most from this program:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Complete daily content when possible (but progress at your own pace)</li>
            <li>Track your mood, wellness, and other metrics regularly</li>
            <li>Engage with your assigned provider as recommended</li>
            <li>Utilize crisis resources immediately if you experience thoughts of self-harm</li>
            <li>Update your safety plan regularly</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Crisis Support</h3>
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <p className="font-semibold text-red-800 mb-2">
              🚨 If you are experiencing a mental health crisis or thoughts of self-harm:
            </p>
            <ul className="text-red-700 space-y-1">
              <li>• Call 988 (Suicide & Crisis Lifeline) - Available 24/7</li>
              <li>• Text "HELLO" to 741741 (Crisis Text Line)</li>
              <li>• Call 911 for immediate emergency assistance</li>
              <li>• Go to your nearest emergency room</li>
            </ul>
            <p className="text-red-700 mt-2">
              This program is NOT a substitute for emergency mental health care. Always seek immediate help in crisis situations.
            </p>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Limitations of Service</h3>
          <p className="mb-4">
            Please understand:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>This is a supplemental mental wellness companion program, not a replacement for professional treatment</li>
            <li>Continue all prescribed medications and therapy as directed by your healthcare providers</li>
            <li>The platform provides educational content and tracking tools, not direct medical advice</li>
            <li>Your provider will communicate with you through the platform but may not respond immediately</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Voluntary Participation</h3>
          <p className="mb-4">
            Your participation is completely voluntary:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>You may discontinue the program at any time</li>
            <li>You may request to be unassigned from your current provider</li>
            <li>You can export your data before leaving the program</li>
            <li>Declining this acknowledgment will log you out, but you can return later</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Data & Confidentiality</h3>
          <p className="mb-4">
            Your data is handled with care:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>All data is encrypted and HIPAA-compliant</li>
            <li>Your provider can only see data you consent to share</li>
            <li>Your personal information will not be sold or shared with third parties</li>
            <li>You can request data deletion in accordance with our privacy policy</li>
          </ul>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-8 mb-6">
            <h4 className="font-semibold text-blue-900 mb-2">By acknowledging below, you confirm that:</h4>
            <ul className="text-blue-800 space-y-1">
              <li>✓ You have read and understand this enrollment agreement</li>
              <li>✓ You understand your relationship with {enrolledBy}</li>
              <li>✓ You understand this is supplemental support, not emergency care</li>
              <li>✓ You know how to access crisis resources if needed</li>
              <li>✓ You voluntarily agree to participate in the MHDF Momentum program</li>
              <li>✓ You understand your privacy rights and consent options</li>
            </ul>
          </div>

          {!hasScrolled && (
            <div className="bg-gray-100 text-gray-600 text-center py-3 mb-4 rounded">
              ↓ Please scroll to the bottom to continue ↓
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Acknowledgement Checkbox */}
        <div className="px-8 pb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-gray-700">
              I have read, understood, and agree to the terms outlined in this Client Enrollment Acknowledgment. 
              I understand my relationship with {enrolledBy} and voluntarily agree to participate in the 
              MHDF Momentum mental wellness companion program.
            </span>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="bg-gray-50 px-8 py-6 rounded-b-lg flex justify-between items-center">
          <button
            onClick={handleDecline}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            Decline & Logout
          </button>
          
          <button
            onClick={handleAcknowledge}
            disabled={!acknowledged || !hasScrolled || loading}
            className={`px-8 py-2 rounded-lg font-semibold transition ${
              acknowledged && hasScrolled && !loading
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? 'Processing...' : 'Acknowledge & Continue'}
          </button>
        </div>
      </div>

      {/* Decline Confirmation Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Decline Enrollment?</h3>
            <p className="text-gray-700 mb-6">
              If you decline this acknowledgment, you will be logged out and cannot access the program. 
              You can return later to review and acknowledge if you change your mind.
            </p>
            <p className="text-gray-700 mb-6">
              If you have concerns about your enrollment, please contact {enrolledBy} directly.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDecline}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmDecline}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Confirm & Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientEnrollmentAcknowledgement;
